/*
  Glooty "Buy 2, get 1 free" box in the bag (snippets/glooty-b2g1-cart.liquid, part 'box'), in the cart
  drawer and on the cart page. The box is redrawn with the bag (cart-drawer-items, the cart page
  .js-contents), so connectedCallback runs again after every change and the state always comes from the
  Liquid of the current bag.

  1. Code: once the bag has 2+ pieces the offer code (data-code) goes on the bag, and the expired codes of
     earlier offers come off, with POST /cart/update.js { discount: 'CODE,…' } (other codes she has stay).
     Each bag state is checked once per page, so nothing can loop. With 3+ pieces the bag is repainted
     with the sections the request returns, so the FREE line and the savings show straight away. If the
     box still says it is applying the free piece after that, it falls back to a plain line.
  2. Order bump (2, 5, 8… pieces): one small card per product (radio). Under the row, the picked card's
     line says what the button adds (name, colour · size, pre-filled) next to the add button; its
     colour pebbles and option pills open only when she taps a card or that line, so the box stays
     short and her own bag lines show. The button adds the variant with /cart/add.js and the drawer /
     cart page sections, then the bag is repainted the way Dawn does it.
  3. On the cart page the header still has the cart drawer, and Dawn only redraws the bag she is using
     (cart.js publishes cartUpdate with source 'cart-items', which the other bag skips). So after each
     change there, the other bag is redrawn too: the drawer after a change on the page, the page after
     a change in the open drawer. Without it the drawer kept a FREE line and savings that were gone.
  Repaints follow Dawn (assets/cart.js onCartUpdate / updateQuantity, assets/cart-drawer.js). Needs
  Dawn's global.js (fetchConfig, trapFocus) and pubsub.js / constants.js (PUB_SUB_EVENTS).
*/
if (!customElements.get('glooty-b2g1')) {
  // Codes of earlier offers, expired: taken off any bag that still carries them
  const EXPIRED_CODES = ['WINGWOMAN', 'SECONDCOLOR'];
  const checked = new Set();
  let queue = Promise.resolve();
  // Codes on the bag as of the last cart answer (null = not known on this page yet)
  let bagCodes = null;
  const rememberCodes = (cart) => {
    if (cart && Array.isArray(cart.discount_codes)) bagCodes = cart.discount_codes.map((entry) => entry.code);
    return cart;
  };

  const same = (a, b) =>
    String(a || '')
      .trim()
      .toUpperCase() ===
    String(b || '')
      .trim()
      .toUpperCase();

  const route = (name, fallback) => (window.routes && window.routes[name]) || fallback;

  const postJson = (url, body) => {
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('json')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    config.body = JSON.stringify(body);
    return fetch(url, config).then((response) => response.json());
  };

  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  // Sections a cart request should return: the drawer, the header bubble and, on the cart page, its
  // items and footer sections (Section Rendering API ids)
  const sectionsToRender = () => {
    const ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    ['main-cart-items', 'main-cart-footer'].forEach((id) => {
      const wrap = document.getElementById(id);
      if (wrap && wrap.dataset.id) ids.push(wrap.dataset.id);
    });
    return ids;
  };

  // Where focus was before a repaint she did not ask for (the code auto-apply), by id or by the
  // control's name inside its cart row, so it comes back after the swap
  const focusMark = () => {
    const active = document.activeElement;
    if (!active || active === document.body) return null;
    if (active.id) return { id: active.id };
    const row = active.closest('[id^="CartDrawer-Item-"], [id^="CartItem-"]');
    const name = active.getAttribute('name');
    return row && name ? { row: row.id, name } : null;
  };

  const focusRestore = (mark) => {
    if (!mark || (document.activeElement && document.activeElement !== document.body)) return;
    let target = mark.id ? document.getElementById(mark.id) : null;
    if (!target && mark.row) {
      const row = document.getElementById(mark.row);
      target = row && row.querySelector(`[name="${mark.name}"]`);
    }
    if (target) focusInBag(target);
  };

  // Focus inside the open drawer keeps Dawn's focus trap
  const focusInBag = (target) => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.contains(target) && drawer.classList.contains('active') && typeof trapFocus === 'function') {
      trapFocus(document.getElementById('CartDrawer') || drawer, target);
    } else {
      target.focus();
    }
  };

  // Same swaps as Dawn: drawer header (its count pill), items and totals; the header bubble; the cart
  // page .js-contents. The drawer's scroll position is kept.
  const paint = (sections) => {
    if (!sections) return;

    const drawerHtml = sections['cart-drawer'];
    const drawer = document.querySelector('cart-drawer');
    if (drawerHtml && drawer) {
      const html = parse(drawerHtml);
      const liveItems = drawer.querySelector('cart-drawer-items');
      const scrollTop = liveItems ? liveItems.scrollTop : 0;
      ['.drawer__header', 'cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
        const target = drawer.querySelector(selector);
        const source = html.querySelector(selector);
        if (target && source) target.replaceWith(source);
      });
      const newItems = drawer.querySelector('cart-drawer-items');
      if (newItems) newItems.scrollTop = scrollTop;
      const sourceDrawer = html.querySelector('cart-drawer');
      const inner = drawer.querySelector('.drawer__inner');
      if (sourceDrawer && inner) {
        const isEmpty = sourceDrawer.classList.contains('is-empty');
        drawer.classList.toggle('is-empty', isEmpty);
        const checkout = document.getElementById('CartDrawer-Checkout');
        if (checkout) checkout.disabled = isEmpty;
        const sourceEmpty = sourceDrawer.querySelector('.drawer__inner-empty');
        const liveEmpty = inner.querySelector('.drawer__inner-empty');
        if (liveEmpty && !sourceEmpty) liveEmpty.remove();
        if (!liveEmpty && sourceEmpty) inner.prepend(sourceEmpty);
      }
    }

    const bubbleHtml = sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      const source = parse(bubbleHtml).querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    ['main-cart-items', 'main-cart-footer'].forEach((id) => {
      const wrap = document.getElementById(id);
      const html = wrap && sections[wrap.dataset.id];
      if (!html) return;
      const target = wrap.querySelector('.js-contents');
      const source = parse(html).querySelector('.js-contents');
      if (target && source) target.innerHTML = source.innerHTML;
    });
  };

  // The bag was repainted here: other listeners (quantity rules on the product page…) hear about it,
  // while Dawn's cart-items / cart-drawer-items skip a second redraw (source 'cart-items'). Every bag
  // was in the repaint, so the cart page sync below skips it too (glootyB2G1).
  const announce = (cartData) => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData, glootyB2G1: true });
    }
  };

  // Cart page with the cart drawer in the header: after a change in one bag, redraw the other one
  // (see 3. above). The drawer was the one in use when it is open.
  const syncOtherBag = (event) => {
    if (event && event.glootyB2G1) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !document.getElementById('main-cart-items')) return;
    const ids = drawer.classList.contains('active')
      ? sectionsToRender().filter((id) => id !== 'cart-drawer' && id !== 'cart-icon-bubble')
      : ['cart-drawer'];
    if (!ids.length) return;
    fetch(`${route('cart_url', '/cart')}?sections=${ids.join(',')}`)
      .then((response) => response.json())
      .then((sections) => paint(sections))
      .catch(() => null);
  };

  if (
    document.getElementById('main-cart-items') &&
    document.querySelector('cart-drawer') &&
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined'
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncOtherBag);
  }

  const piecesOf = (cart) =>
    (cart.items || []).reduce((count, item) => count + (item.original_price > 0 ? item.quantity : 0), 0);

  // Code on (2+ pieces) and expired codes off. Resolves when done; repaints when it changed a bag of 3+
  const syncCode = (code) =>
    fetch(`${route('cart_url', '/cart')}.js`)
      .then((response) => response.json())
      .then(rememberCodes)
      .then((cart) => {
        const codes = (cart.discount_codes || []).map((entry) => entry.code);
        const pieces = piecesOf(cart);
        const want = codes.filter((value) => !EXPIRED_CODES.some((expired) => same(expired, value)));
        if (code && pieces >= 2 && !want.some((value) => same(value, code))) want.push(code);
        const changed = want.length !== codes.length || want.some((value, index) => !same(value, codes[index]));
        if (!changed) return null;

        const repaint = pieces >= 3;
        const body = { discount: want.join(',') };
        if (repaint) {
          body.sections = sectionsToRender();
          body.sections_url = window.location.pathname;
        }
        return postJson(`${route('cart_update_url', '/cart/update')}.js`, body).then((response) => {
          rememberCodes(response);
          if (repaint && response && response.sections) {
            const mark = focusMark();
            paint(response.sections);
            focusRestore(mark);
            announce(response);
          }
          return response;
        });
      });

  class GlootyB2G1 extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      // The radios must not reach Dawn's cart-items 'change' handler (it reads quantity inputs)
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.querySelectorAll('[data-g-panel]').forEach((panel) => this.refresh(panel));
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    // Each bag state once per page (the drawer and the cart page share it)
    sync() {
      const code = (this.dataset.code || '').trim();
      const key = `${code}|${this.dataset.sig}`;
      if (!checked.has(key)) {
        checked.add(key);
        queue = queue.then(() => syncCode(code)).catch(() => null);
      }
      queue = queue.then(() => this.settle());
    }

    // Still "Applying your free piece…" once the code is sorted: nothing was allocated, so no claim
    settle() {
      if (!this.isConnected || !this.hasAttribute('data-pending')) return;
      const title = this.querySelector('[data-g-title]');
      if (title && this.dataset.fallback) title.textContent = this.dataset.fallback;
      this.removeAttribute('data-pending');
    }

    onChange(event) {
      event.stopPropagation();
      const input = event.target;
      if (input.matches('[data-g-pick]')) {
        this.pick(Number(input.value));
        return;
      }
      const panel = input.closest('[data-g-panel]');
      if (!panel) return;
      this.setMessage(panel, '');
      const field = input.closest('.g-b2g1__field');
      if (field) field.classList.remove('is-alert');
      this.refresh(panel);
      this.reveal(this.querySelector(`[data-g-card="${panel.dataset.gPanel}"]`));
    }

    onClick(event) {
      const button = event.target.closest('[data-g-add]');
      if (button) {
        this.add(button.closest('[data-g-panel]'), button);
        return;
      }
      const toggle = event.target.closest('[data-g-toggle]');
      if (toggle) {
        this.setOpen(toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      // A tap on a card, the picked one too (no 'change' then), opens its colour and size
      if (event.target.closest('[data-g-card]')) this.setOpen(true);
    }

    // Card picked: its panel shows, the others hide; its colour and size open
    pick(index) {
      this.querySelectorAll('[data-g-panel]').forEach((panel) => {
        panel.hidden = Number(panel.dataset.gPanel) !== index;
      });
      this.querySelectorAll('[data-g-card]').forEach((card) => {
        card.classList.toggle('is-selected', Number(card.dataset.gCard) === index);
      });
      this.setOpen(true);
    }

    // Colour pebbles and option pills of the picked card, shown or not (closed by default)
    setOpen(open) {
      const bump = this.querySelector('.g-b2g1__bump');
      if (!bump) return;
      bump.classList.toggle('is-open', open);
      this.querySelectorAll('[data-g-toggle]').forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
    }

    // The picked card back in view when the row was swiped away from it
    reveal(card) {
      const row = card && card.parentElement;
      if (!row) return;
      const box = row.getBoundingClientRect();
      const rect = card.getBoundingClientRect();
      if (rect.left < box.left) row.scrollBy({ left: rect.left - box.left, behavior: 'smooth' });
      else if (rect.right > box.right) row.scrollBy({ left: rect.right - box.right, behavior: 'smooth' });
    }

    variants(panel) {
      if (!panel.gVariants) {
        const data = panel.querySelector('[data-g-variants]');
        try {
          panel.gVariants = JSON.parse(data ? data.textContent : '[]');
        } catch (error) {
          panel.gVariants = [];
        }
      }
      return panel.gVariants;
    }

    // Values picked by option position (null = not picked yet)
    chosen(panel) {
      const values = [];
      panel.querySelectorAll('input[data-g-opt]:checked').forEach((input) => {
        values[Number(input.dataset.gOpt)] = input.value;
      });
      return values;
    }

    match(panel, values, ignoreMissing) {
      return this.variants(panel).find((variant) =>
        variant.o.every((value, index) => (values[index] == null ? ignoreMissing : value === values[index]))
      );
    }

    // Photo, colour name, badge, button label and the pills that cannot be bought, for the picks so far
    refresh(panel) {
      const values = this.chosen(panel);
      const variant = this.match(panel, values, true);
      const card = this.querySelector(`[data-g-card="${panel.dataset.gPanel}"]`);

      const colorName = panel.querySelector('[data-g-color-name]');
      const colorInput = panel.querySelector('.g-b2g1__peb input:checked');
      if (colorName && colorInput) colorName.textContent = colorInput.value;

      if (variant && card) {
        const img = card.querySelector('[data-g-img]');
        if (img && variant.img && img.getAttribute('src') !== variant.img) img.src = variant.img;
        const badge = card.querySelector('[data-g-badge]');
        if (badge) {
          badge.textContent = variant.x || 'FREE';
          badge.classList.toggle('g-b2g1__badge--plus', Boolean(variant.x));
        }
        const label = panel.querySelector('[data-g-add-label]');
        if (label) label.textContent = variant.x ? `Add for ${variant.x}` : 'Add free';
      }

      // A pill whose variant (with the other picks) cannot be bought is struck through and disabled
      panel.querySelectorAll('.g-b2g1__pill input').forEach((input) => {
        const index = Number(input.dataset.gOpt);
        const test = values.slice();
        test[index] = input.value;
        const option = this.match(panel, test, true);
        const available = Boolean(option && option.a);
        input.disabled = !available;
        if (!available && input.checked) input.checked = false;
      });

      // The line next to the button: the picks so far, "Pick size" for one still missing
      const sum = panel.querySelector('[data-g-sum]');
      if (sum) {
        sum.textContent = [...panel.querySelectorAll('.g-b2g1__field')]
          .map((field) => {
            const input = field.querySelector('input:checked');
            return input ? input.value : `Pick ${field.dataset.gLabel || 'an option'}`;
          })
          .join(' · ');
      }
    }

    setMessage(panel, text) {
      const box = panel && panel.querySelector('[data-g-msg]');
      if (!box) return;
      box.textContent = '';
      // a fresh text node, so screen readers announce the same message twice in a row
      if (text) window.setTimeout(() => (box.textContent = text), 30);
    }

    add(panel, button) {
      if (!panel || button.classList.contains('loading')) return;
      const values = this.chosen(panel);
      const options = this.variants(panel)[0] ? this.variants(panel)[0].o.length : 0;

      // An option still to pick (the size): say so and take her there
      for (let index = 0; index < options; index += 1) {
        if (values[index] != null) continue;
        const field = panel.querySelector(`[data-g-field="${index}"]`);
        const fieldset = field && field.closest('.g-b2g1__field');
        this.setOpen(true);
        if (fieldset) {
          fieldset.classList.remove('is-alert');
          void fieldset.offsetWidth;
          fieldset.classList.add('is-alert');
          const first = field.querySelector('input:not(:disabled)');
          if (first) first.focus();
        }
        this.setMessage(panel, this.dataset.msgSize);
        return;
      }

      const variant = this.match(panel, values, false);
      if (!variant || !variant.a) {
        this.setMessage(panel, this.dataset.msgError);
        return;
      }

      const spinner = button.querySelector('.loading__spinner');
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
      if (spinner) spinner.classList.remove('hidden');
      this.setMessage(panel, '');

      const scope = this.dataset.scope;
      const code = (this.dataset.code || '').trim();
      const linesUpdate = this.createLinesUpdateEvent(variant.id);

      queue = queue
        .then(() =>
          postJson(`${route('cart_add_url', '/cart/add')}.js`, {
            items: [{ id: variant.id, quantity: 1 }],
            sections: sectionsToRender(),
            sections_url: window.location.pathname,
          })
        )
        .then((response) => {
          if (response.status) {
            this.setMessage(panel, response.description || response.message || this.dataset.msgError);
            linesUpdate?.reject(new Error(response.description || response.message || 'add failed'));
            return;
          }
          this.resolveLinesUpdate(linesUpdate);
          // The code is normally on the bag already (put there at 2 pieces), so the returned sections
          // already show the free piece; if it is not, put it on and repaint with those sections
          if (!code || (bagCodes && bagCodes.some((value) => same(value, code)))) {
            return Promise.resolve(response.sections).then((sections) => {
              paint(sections);
              announce(response);
              this.focusAfterPaint(scope);
            });
          }
          return fetch(`${route('cart_url', '/cart')}.js`)
            .then((cartResponse) => cartResponse.json())
            .then(rememberCodes)
            .then((cart) => {
              const codes = (cart.discount_codes || []).map((entry) => entry.code);
              if (codes.some((value) => same(value, code))) return response.sections;
              const keep = codes.filter((value) => !EXPIRED_CODES.some((expired) => same(expired, value)));
              return postJson(`${route('cart_update_url', '/cart/update')}.js`, {
                discount: keep.concat(code).join(','),
                sections: sectionsToRender(),
                sections_url: window.location.pathname,
              })
                .then(rememberCodes)
                .then((updated) => (updated && updated.sections) || response.sections);
            })
            .catch(() => response.sections)
            .then((sections) => {
              paint(sections);
              announce(response);
              this.focusAfterPaint(scope);
            });
        })
        .catch(() => {
          this.setMessage(panel, this.dataset.msgError);
          linesUpdate?.reject(new Error('network'));
        })
        .finally(() => {
          if (!button.isConnected) return;
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    // Shopify's standard cart events (analytics), as Dawn's product form sends them
    createLinesUpdateEvent(variantId) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: this.closest('cart-drawer') ? 'dialog' : 'cart',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
      return deferred;
    }

    resolveLinesUpdate(deferred) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!deferred || !CartLinesUpdateEvent) return;
      fetch(`${route('cart_url', '/cart')}.json`)
        .then((response) => response.json())
        .then((cart) => {
          if (!cart || !cart.currency) throw new Error('Missing currency in cart response');
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        })
        .catch((error) => deferred.reject(error));
    }

    // The bag was redrawn: focus goes to the new line of the same box (the drawer keeps its trap)
    focusAfterPaint(scope) {
      const container = scope === 'page' ? document.getElementById('main-cart-items') : document.getElementById('CartDrawer');
      const target =
        (container && container.querySelector('glooty-b2g1 [data-g-title]')) ||
        (container && container.querySelector('.cart-item__name'));
      if (target) focusInBag(target);
    }
  }

  customElements.define('glooty-b2g1', GlootyB2G1);
}
